import { useState } from 'react'
import { Card } from '../ui/Card'
import { Input } from '../ui/Input'
import { Button } from '../ui/Button'
import { Badge } from '../ui/Badge'
import { Search, Plus } from 'lucide-react'

interface Product {
  id: string
  name: string
  sku: string
  price: number | string
  stock_quantity: number
  category_name?: string
  gst_rate?: number
}

interface MobileProductSearchProps {
  products: Product[]
  onAddToCart: (product: Product) => void
  formatCurrency: (amount: number) => string
  onQuickAdd?: () => void
}

export const MobileProductSearch = ({
  products,
  onAddToCart,
  formatCurrency,
  onQuickAdd
}: MobileProductSearchProps) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [showDropdown, setShowDropdown] = useState(false)

  const filteredProducts = products.filter(product =>
    product.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.sku?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.category_name?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  return (
    <Card className="p-3">
      <div className="space-y-2">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4 z-10" />
          <Input
            type="text"
            placeholder="Search products..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value)
              setShowDropdown(e.target.value.length > 0)
            }}
            onFocus={() => setShowDropdown(searchTerm.length > 0)}
            onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
            className="pl-10 h-10 text-sm"
          />
        
        {/* Mobile-optimized dropdown */}
        {showDropdown && searchTerm && (
          <div className="absolute top-full left-0 right-0 z-40 mt-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-80 overflow-y-auto">
            {filteredProducts.slice(0, 10).map(product => {
              const price = parseFloat(product.price?.toString() || '0')
              const isLowStock = product.stock_quantity <= 10
              
              return (
                <div
                  key={product.id}
                  className="hover:bg-orange-50 dark:hover:bg-orange-900/20 border-b border-gray-100 dark:border-gray-700 last:border-b-0"
                >
                  <div className="p-3">
                    <div className="flex-1">
                      <div className="font-medium text-sm text-gray-900 dark:text-gray-100 truncate mb-1">
                        {product.name}
                      </div>
                      <div className="flex items-center gap-1 mb-2 text-xs">
                        <span className="text-gray-500 dark:text-gray-400 truncate">
                          {product.category_name}
                        </span>
                        {product.sku && (
                          <>
                            <span className="text-gray-400">•</span>
                            <span className="text-gray-500 dark:text-gray-400 truncate">
                              {product.sku}
                            </span>
                          </>
                        )}
                        {typeof product.gst_rate === 'number' && (
                          <>
                            <span className="text-gray-400">•</span>
                            <span className="text-gray-500 dark:text-gray-400">
                              GST {product.gst_rate}%
                            </span>
                          </>
                        )}
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-base font-bold text-orange-600 dark:text-orange-400">
                          {formatCurrency(price)}
                        </span>
                        <div className="flex items-center gap-2">
                          <Badge 
                            variant={isLowStock ? 'warning' : 'secondary'}
                            size="sm"
                            className="text-xs"
                          >
                            Stock: {product.stock_quantity}
                          </Badge>
                          <Button
                            onClick={(e) => {
                              e.stopPropagation()
                              onAddToCart(product)
                              setSearchTerm('')
                              setShowDropdown(false)
                            }}
                            disabled={product.stock_quantity <= 0}
                            className="h-7 w-12 rounded-full flex-shrink-0 text-xs"
                            size="sm"
                          >
                            <Plus className="h-3 w-3 mr-1" />
                            Add
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )
            })}
            {filteredProducts.length === 0 && (
              <div className="p-6 text-center text-gray-500 dark:text-gray-400">
                <Search className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No products found</p>
              </div>
            )}
            {filteredProducts.length > 10 && (
              <div className="p-3 text-center text-sm text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700">
                Showing first 10 results. Refine your search for more.
              </div>
            )}
          </div>
        )}
        </div>

        {/* Quick Add Button */}
        {onQuickAdd && (
          <Button
            onClick={onQuickAdd}
            size="sm"
            className="w-full h-9 text-xs bg-orange-600 hover:bg-orange-700 flex items-center justify-center gap-1"
          >
            <Plus className="h-3 w-3" />
            Add Custom Product
          </Button>
        )}
      </div>
    </Card>
  )
}